import type { IElement } from '../models/project.model';
import type { ProductRecord } from '../models/product.model';
import type { Recipe, RecipeID } from '../models/recipe.model';
import {
  ProjectChangeType,
  type ProjectChange,
  type ProjectChangeMap,
} from './pipeline/update-engine.models';
import { required } from './function.helpers';
import { reportSharedError } from './error-reporting.helpers';
import {
  applyRecipeById,
  type ApplyRecipeOptions,
  clearRecipe,
  detachRecipe,
  getProposalRecipeApplyPayloadElements,
  hostHasRecipeComposition,
  isAutoRecipeId,
  overwriteRecipe,
  overwriteRecipeForProposal,
  removeDeletedProductRefsInRecipe,
} from './recipe.helpers';

/**
 * Structural overwrite strategy shared by {@link resolveRecipePreviewElement} (UI preview) and the
 * {@link ProjectChangeType.ElementRecipeApply} handler (persist), so both stay in lockstep:
 * `'baseline'` clean-slates element-only state; `'proposal'` keeps baseline properties the recipe omits.
 */
export type RecipeOverwriteMode = 'baseline' | 'proposal';

export type BuildRecipeProjectChangeOptions = {
  elementId: string;
  recipeId: RecipeID;
  recipes: Recipe[];
  products: ProductRecord;
  contextElement: IElement;
  /**
   * Preview from {@link resolveRecipePreviewElement} computed with the same overwrite mode that will
   * persist; its element tree is embedded into proposal applies so proposal resolve matches the UI.
   */
  previewElement: IElement;
  selectedProposalId?: string;
  applyOptions?: ApplyRecipeOptions;
  /** Regenerated {@link IElement.fallbackName} to persist on an apply (omit for detach/clear). */
  fallbackName?: string;
};

export type BuildRecipeProjectChangeResult = {
  /**
   * The {@link ProjectChange} to hand to `applyChanges`, which routes it to baseline or the
   * active proposal. `undefined` when there is nothing to persist (e.g. unresolved auto recipe).
   */
  change?: ProjectChange;
};

type ElementRecipeApplyChange =
  ProjectChangeMap[ProjectChangeType.ElementRecipeApply];

/**
 * Catalog or resolved recipe id carried by an {@link ElementRecipeApplyChange} payload.
 */
export const getAppliedRecipeIdFromChange = (
  change: Pick<ElementRecipeApplyChange, 'recipeId' | 'recipe'>,
): string => change.recipe.id ?? change.recipeId;

/**
 * True when {@link element} already carries the child tree that {@link recipe} defines.
 *
 * An element can reference a recipe while holding none of its composition — leftover dummy
 * children from a partial project copy, or an apply that never materialized. Treating those as
 * "already applied" makes re-selecting the same recipe a no-op, so the broken tree can never be
 * repaired from the UI. Composition uses the same check as compute stamping
 * ({@link hostHasRecipeComposition}): a `:r:` child or a product descendant. Dummy leftovers and
 * hidden previous-state rows without products do not count.
 *
 * @param element - Element carrying {@link IElement.recipe_id}
 * @param recipe - Target recipe, or `undefined` when it cannot be resolved from the catalog
 * @returns `false` only when the recipe defines children and the element has no composition
 */
const hasMaterializedRecipeChildren = (
  element: IElement,
  recipe: Pick<Recipe, 'elements'> | undefined,
): boolean => {
  if (!recipe || recipe.elements.length === 0) return true;
  return hostHasRecipeComposition(element);
};

/**
 * True when the element already references this recipe id *and* holds its children, so
 * re-applying would be a no-op. An element whose recipe children are missing is not redundant —
 * see {@link hasMaterializedRecipeChildren}.
 */
export const isRedundantElementRecipeApplyAgainstElement = (
  element: IElement,
  recipeId: RecipeID,
  recipes: Recipe[],
  products: ProductRecord,
  applyOptions?: ApplyRecipeOptions,
): boolean => {
  if (recipeId === 'detach' || recipeId === 'none') return false;
  const currentId = element.recipe_id;
  if (!currentId) return false;

  const targetId = isAutoRecipeId(recipeId)
    ? resolveBaselineRecipePayload(
        recipes,
        products,
        element,
        recipeId,
        applyOptions,
      )?.id
    : recipeId;

  if (!targetId || targetId !== currentId) return false;

  return hasMaterializedRecipeChildren(
    element,
    recipes.find((candidate) => candidate.id === targetId),
  );
};

/**
 * True when a stored or incoming {@link ElementRecipeApplyChange} matches {@link IElement.recipe_id}
 * and the element already holds the payload recipe's children — see
 * {@link hasMaterializedRecipeChildren}.
 */
export const isRedundantElementRecipeApplyChange = (
  element: IElement,
  change: Pick<ElementRecipeApplyChange, 'recipeId' | 'recipe'>,
): boolean => {
  const currentId = element.recipe_id;
  if (!currentId) return false;
  if (currentId !== getAppliedRecipeIdFromChange(change)) return false;

  return hasMaterializedRecipeChildren(element, change.recipe);
};

/**
 * Recipe definition for baseline overwrite ({@link overwriteRecipe}), without
 * preview-stamped element trees (those belong on proposal {@link ElementRecipeApply} only).
 */
export const resolveBaselineRecipePayload = (
  recipes: Recipe[],
  products: ProductRecord,
  element: IElement,
  recipeId: RecipeID,
  applyOptions?: ApplyRecipeOptions,
): Recipe | undefined => {
  if (recipeId === 'detach' || recipeId === 'none') return undefined;
  if (isAutoRecipeId(recipeId)) {
    const resolved = applyRecipeById(
      recipes,
      products,
      element,
      recipeId,
      applyOptions,
    );
    const resolvedId = resolved.recipe_id;
    if (!resolvedId) return undefined;
    const match = recipes.find((candidate) => candidate.id === resolvedId);
    return match
      ? removeDeletedProductRefsInRecipe(products, match)
      : undefined;
  }
  const match = recipes.find((candidate) => candidate.id === recipeId);
  return match ? removeDeletedProductRefsInRecipe(products, match) : undefined;
};

/**
 * Recipe payload for {@link ProjectChangeType.ElementRecipeApply}, optionally
 * embedding a preview-stamped element tree so proposal resolve matches the UI.
 *
 * Hidden previous-state children and DEV-3350 user-added siblings on
 * {@link appliedParent} are omitted; {@link overwriteRecipeForProposal}
 * re-derives them from the pre-apply element on replay.
 *
 * @returns `undefined` when the recipe cannot be resolved, or when a preview that should carry the
 *   applied addresses carries none — see the reported case below. The caller persists no change.
 */
export const buildProposalRecipeApplyPayload = (
  recipes: Recipe[],
  products: ProductRecord,
  recipeId: RecipeID,
  appliedParent?: IElement,
): Recipe | undefined => {
  const recipe = recipes.find((candidate) => candidate.id === recipeId);
  if (!recipe) return undefined;

  const sanitized = removeDeletedProductRefsInRecipe(products, recipe);
  if (!appliedParent?.elements.length) return sanitized;

  const payloadElements = getProposalRecipeApplyPayloadElements(
    appliedParent,
    sanitized,
  );
  if (payloadElements.length === 0) {
    // The recipe defines nothing to stamp, so an empty payload is the correct one.
    if (sanitized.elements.length === 0) return sanitized;

    // A preview of this recipe always carries the applied addresses, so reaching this means the
    // preview belongs to another element or recipe. Both payloads we could build here are worse
    // than none: the unfiltered catalog re-stamps user-added siblings as extra `:r:` ingredients
    // (double CO2e), and an empty child list reads as "already materialized" in
    // {@link hasMaterializedRecipeChildren}, which makes the apply no-op on replay, lets
    // `dropNoOpChangesAgainstBaseline` drop the action, and leaves the host with `recipe_id` and
    // no composition — the shape this whole flow exists to repair (DEV-3656).
    reportSharedError(
      new Error('Recipe apply payload matched no applied child address'),
      {
        elementId: appliedParent.id,
        recipeId: sanitized.id,
        previewChildIds: appliedParent.elements.map((child) => child.id),
      },
    );
    return undefined;
  }

  return { ...sanitized, elements: payloadElements };
};

/**
 * Preview element state for a recipe action before persisting. The preview mirrors exactly what the
 * {@link ProjectChangeType.ElementRecipeApply} handler will persist for the given {@link RecipeOverwriteMode}
 * (UI and data parity): `'baseline'` resolves the catalog recipe and clean-slates via {@link overwriteRecipe};
 * `'proposal'` keeps baseline-only properties via {@link overwriteRecipeForProposal}.
 */
export const resolveRecipePreviewElement = (
  recipes: Recipe[],
  products: ProductRecord,
  element: IElement,
  recipeId: RecipeID,
  applyOptions?: ApplyRecipeOptions,
  overwriteMode: RecipeOverwriteMode = 'proposal',
): IElement => {
  if (recipeId === 'detach') return detachRecipe(element);
  if (recipeId === 'none') return clearRecipe(element);

  // Baseline preview must equal the persisted overwriteRecipe(element, catalog) result.
  if (overwriteMode === 'baseline') {
    const baselineRecipe = resolveBaselineRecipePayload(
      recipes,
      products,
      element,
      recipeId,
      applyOptions,
    );
    return baselineRecipe ? overwriteRecipe(element, baselineRecipe) : element;
  }

  if (isAutoRecipeId(recipeId)) {
    return applyRecipeById(recipes, products, element, recipeId, applyOptions);
  }
  const match = required(
    recipes.find((candidate) => candidate.id === recipeId),
  );
  const recipe = removeDeletedProductRefsInRecipe(products, match);
  return overwriteRecipeForProposal(element, recipe, applyOptions);
};

/**
 * Builds the single {@link ProjectChange} that applies the recipe, from a preview the caller already
 * computed with {@link resolveRecipePreviewElement} (same overwrite mode as persist).
 *
 * The caller hands the change straight to `applyChanges`, which routes it to baseline (no proposal
 * selected) or records it as a proposal action — so this builder does not branch on persistence:
 * - `detach` / `none` emit {@link ProjectChangeType.ElementRecipeDetach} / {@link ProjectChangeType.ElementRecipeClear}.
 * - No proposal: {@link ProjectChangeType.ElementRecipeApply} with the catalog recipe and
 *   `overwriteMode: 'baseline'` (clean-slate {@link overwriteRecipe} on baseline).
 * - Proposal selected: {@link ProjectChangeType.ElementRecipeApply} embedding the preview-stamped
 *   element tree so proposal resolve matches the UI.
 *
 * {@link BuildRecipeProjectChangeOptions.fallbackName}, when provided, is baked onto the apply change
 * so the regenerated name persists with the structural apply.
 */
export const buildRecipeProjectChange = (
  options: BuildRecipeProjectChangeOptions,
): BuildRecipeProjectChangeResult => {
  const {
    elementId,
    recipeId,
    recipes,
    products,
    contextElement,
    previewElement,
    selectedProposalId,
    applyOptions,
    fallbackName,
  } = options;

  if (recipeId === 'detach') {
    return {
      change: { type: ProjectChangeType.ElementRecipeDetach, elementId },
    };
  }
  if (recipeId === 'none') {
    return {
      change: { type: ProjectChangeType.ElementRecipeClear, elementId },
    };
  }

  if (
    isRedundantElementRecipeApplyAgainstElement(
      contextElement,
      recipeId,
      recipes,
      products,
      applyOptions,
    )
  ) {
    return {};
  }

  if (!selectedProposalId) {
    const baselineRecipe = resolveBaselineRecipePayload(
      recipes,
      products,
      contextElement,
      recipeId,
      applyOptions,
    );
    if (!baselineRecipe) return {};

    return {
      change: {
        type: ProjectChangeType.ElementRecipeApply,
        elementId,
        recipeId,
        recipe: baselineRecipe,
        overwriteMode: 'baseline',
        ...(fallbackName !== undefined && { fallbackName }),
      },
    };
  }

  const recipe = buildProposalRecipeApplyPayload(
    recipes,
    products,
    recipeId,
    previewElement,
  );
  if (!recipe) return {};

  return {
    change: {
      type: ProjectChangeType.ElementRecipeApply,
      elementId,
      recipeId,
      recipe,
      ...(fallbackName !== undefined && { fallbackName }),
    },
  };
};
